// frontend/src/components/detail/tables/InsightsTable.jsx
import React from 'react';
import { Box, Chip, LinearProgress, Typography, IconButton, Tooltip } from '@mui/material';
import { Link as MuiLink } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { slugify } from '../../../utils/slug';
import { DataTable } from '../../tablekit';
import { F } from '../../../utils/qualFmt';

// ---- Column widths (easy to tweak) ----
// Use width for fixed columns; minWidth for long text that should wrap.
// Keep sums reasonable to avoid overflow (DataTable uses tableLayout:'fixed').
const W = {
  parent: {
    category: { minWidth: 240 },        // "Insight"
    occurrences: { width: 120 },        // "Occurrences"
    type: { width: 120 },               // "Type"
    intensityAvg: { minWidth: 280 }     // "Sentiment intensity"
  },
  child: {
    name: { width: 80 },               // "Name"
    date: { width: 60 },               // "Date"
    demographics: { minWidth: 240 },    // "Demographics"
    summary: { minWidth: 300 },         // "Summary"
    quote: { minWidth: 260 },           // "Quote"
    type: { width: 80 },               // "Type"
    expressed: { width: 100 },          // "Sentiment"
    score: { width: 100 },              // "Intensity"
    justification: { minWidth: 280 },   // "Intensity justification"
    open: { width: 64 }                 // "Open"
  }
};


/* ---------- helpers (same logic, but table rendering via Table Kit) ---------- */

function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

function pickJobNode(story, jobName) {
  const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  const target = slugify(jobName || '');
  return arr.find(j => slugify(j?.job_to_be_done_category || '') === target) || null;
}

function parseDateStr(d) {
  if (!d) return 0;
  const parts = String(d).split('-').map(x => parseInt(x, 10));
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts;
    const ts = new Date(yyyy, (mm || 1) - 1, dd || 1).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }
  const ts = Date.parse(d);
  return Number.isNaN(ts) ? 0 : ts;
}

const sentimentPill = (type) => {
  const t = String(type || '').toLowerCase();
  const color =
    t === 'positive' ? 'success' :
    t === 'negative' ? 'error'   :
    'info';
  const label = type ? String(type) : '—';
  return <Chip size="small" color={color} label={label} />;
};

function iterateJobs(story, jobName) {
  const jobs = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  if (!jobName) return jobs; // no pre-filter; page contexts already did it
  const picked = pickJobNode(story, jobName);
  return picked ? [picked] : [];
}

function buildGroups({ customerStories = [], jobName }) {
  const groups = new Map(); // key = insight_category slug → { category, instances: [...] }

  for (const s of customerStories) {
    const jobs = iterateJobs(s, jobName);
    if (!jobs.length) continue;

    for (const job of jobs) {
      const insights = Array.isArray(job?.insights) ? job.insights : [];
      for (const it of insights) {
        const cat = it?.insight_category || 'Uncategorized';
        const slug = slugify(cat);
        if (!groups.has(slug)) groups.set(slug, { category: cat, instances: [] });
        groups.get(slug).instances.push({
          name: s.customer_name || 'Interview',
          date: s.conversation_date || '',
          dateTs: parseDateStr(s.conversation_date || ''),
          demographics: formatDemographics(s.segmentation_data),
          summary: it?.insight_summary || '',
          quote: it?.insight_quote || it?.quote || '—',
          type: it?.insight_sentiment_overall || '',        // Positive | Negative | Wish List
          expressed: it?.insight_sentiment_expressed || '', // e.g., Frustrated, Appreciative
          score: Number(it?.insight_sentiment_score ?? 0) || 0,
          justification: it?.insight_sentiment_justification || '',
          href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        });
      }
    }
  }

  const parents = [];
  for (const [slug, data] of groups.entries()) {
    const instances = data.instances.slice();
    const occurrences = instances.length;

    // Majority type (mode)
    const typeCounts = instances.reduce((acc, r) => {
      const k = String(r.type || '').trim() || '—';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const majorityType = Object.keys(typeCounts).sort((a, b) => typeCounts[b] - typeCounts[a])[0] || '—';

    // Average intensity (0–10)
    const avg = occurrences > 0
      ? (instances.reduce((sum, r) => sum + (r.score || 0), 0) / occurrences)
      : 0;

    // Sort child rows newest first
    instances.sort((a, b) => (b.dateTs || 0) - (a.dateTs || 0));

    parents.push({
      key: slug,
      category: data.category,
      occurrences,
      type: majorityType,
      intensityAvg: avg,
      instances
    });
  }

  parents.sort((a, b) => b.occurrences - a.occurrences);
  return parents;
}

/* ---------- Table Kit wrapper ---------- */

export default function UnifiedInsightsTable({ customerStories = [], jobName, title = 'Insights' }) {
  // build unfiltered rows (contexts already scoped the data)
  const rows = React.useMemo(
    () => buildGroups({ customerStories, jobName }),
    [customerStories, jobName]
  );

  if (!rows.length) return null;

  // Parent columns
  const columns = [
    { key: 'category', header: 'Insight', ...W.parent.category },
    { key: 'occurrences', header: 'Occurrences', type: 'number', align: 'right', ...W.parent.occurrences },
    {
      key: 'type',
      header: 'Type',
      accessor: (r) => r.type || '',
      render: (v) => sentimentPill(v),
      ...W.parent.type
    },
    {
      key: 'intensityAvg',
      header: 'Sentiment intensity',
      type: 'number',
      accessor: (r) => (Number.isFinite(r.intensityAvg) ? r.intensityAvg : null),
      render: (v) =>
        v != null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {F.insightIntensity(v).text}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, (v / 10) * 100))}
              />
            </Box>
          </Box>
        ) : '—',
      ...W.parent.intensityAvg
    }
  ];


  // Sub-table columns (child “instances”)
  const subTables = [
    {
      key: 'instances',
      title: 'Interviews',
      columns: [
        { key: 'name', header: 'Name', ...W.child.name },
        { key: 'date', header: 'Date', type: 'date', ...W.child.date },
        { key: 'demographics', header: 'Demographics', ...W.child.demographics },
        { key: 'summary', header: 'Summary', ...W.child.summary },
        {
          key: 'quote',
          header: 'Quote',
          accessor: (r) => r.quote || '',
          render: (text, row) =>
            text ? (
              <MuiLink
                href={row?.href || '#'}
                underline="hover"
                target="_blank"
                rel="noopener"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                aria-label="Open quote clip"
              >
                <PlayCircleOutlineIcon fontSize="small" />
                <Typography component="span" variant="body2">
                  {text}
                </Typography>
              </MuiLink>
            ) : '—',
          ...W.child.quote
        },
        {
          key: 'type',
          header: 'Type',
          accessor: (r) => r.type || '',
          render: (v) => sentimentPill(v),
          ...W.child.type
        },
        { key: 'expressed', header: 'Sentiment', ...W.child.expressed },
        {
          key: 'score',
          header: 'Intensity',
          type: 'number',
          accessor: (r) => (Number.isFinite(r.score) ? r.score : null),
          render: (v) => (v != null ? F.insightIntensity(v).text : '—'),
          align: 'right',
          ...W.child.score
        },
        { key: 'justification', header: 'Intensity justification', ...W.child.justification },
        {
          key: 'href',
          header: 'Open',
          accessor: (r) => r.href || '',
          render: (href) =>
            href ? (
              <Tooltip title="Open interview">
                <IconButton
                  size="small"
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener"
                  aria-label="Open interview"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : '—',
          align: 'center',
          ...W.child.open
        }
      ]
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={rows}
      subTables={subTables}
      dense
    />
  );
}
